import { Modal, Button, Col, Row, Card } from 'antd';
import capitalize from 'components/utils/capitalize';
import { formatRelative } from 'date-fns';
import React from 'react';
import {
  CreateResource,
  DeleteResource,
  NoChangeResource,
  ResourceEvent,
  ResourceLinkedEvent,
  UpdateResource,
} from 'services/hitchit/types/hike/ResourceEvents';
import createHitchUrl from '../Dashboard/createHitchUrl';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { applyChange } from 'deep-diff';
import {
  ErrorResourceResult,
  OtherResourceResult,
  ResourceResultStatus,
} from 'services/hitchit/types/hike/HikeEvents';
import { ResourceError } from './ResourceError';
import { ResourceSkip } from './ResourceSkip';

// cfr HikeEventsTimeLine
const ResourceCreate = ({
  hitch,
  createdAt,
  resource,
}: {
  hitch: string;
  createdAt?: Date;
  resource: CreateResource;
}) => {
  return (
    <Col>
      <Card>
        <Col>
          <h3>
            {capitalize(resource.resource)} created in {capitalize(hitch)}
          </h3>
        </Col>
        {createdAt && <Col>{capitalize(formatRelative(createdAt, new Date()))}</Col>}
        <Col span={24}>
          <a target="_blank" href={createHitchUrl(hitch, resource)} rel="noreferrer">
            <Button type="primary">{`View ${resource.resource}`}</Button>
          </a>
        </Col>
      </Card>
    </Col>
  );
};

const ResourceNone = ({
  hitch,
  resource,
}: {
  hitch: string;
  createdAt?: Date;
  resource: NoChangeResource;
}) => {
  return (
    <Col>
      <Card>
        <Col>
          <h3>
            {`No changes found in ${capitalize(resource.resource)} from ${capitalize(hitch)}`}
          </h3>
        </Col>
        <Col span={24}>
          <a target="_blank" href={createHitchUrl(hitch, resource)} rel="noreferrer">
            <Button type="primary">{`View ${resource.resource}`}</Button>
          </a>
        </Col>
      </Card>
    </Col>
  );
};

const ResourceUpdate = ({
  hitch,
  createdAt,
  resource,
}: {
  hitch: string;
  createdAt?: Date;
  resource: UpdateResource;
}) => {
  const oldValue = JSON.parse(resource.original);
  const newValue = JSON.parse(resource.original);

  (JSON.parse(resource.diff) as any[]).forEach((change) => applyChange(newValue, true, change));

  return (
    <Col>
      <Card>
        <Col>
          <h3>
            {capitalize(resource.resource)} modified in {capitalize(hitch)}
          </h3>
        </Col>
        {createdAt && <Col>{capitalize(formatRelative(createdAt, new Date()))}</Col>}
        <Col span={24}>
          <Row gutter={32}>
            <Col>
              <Button
                type="primary"
                onClick={() =>
                  Modal.info({
                    width: '80%',
                    title: 'Modified by Hitch and Hike',
                    content: (
                      <ReactDiffViewer
                        oldValue={JSON.stringify(oldValue, null, 2)}
                        newValue={JSON.stringify(newValue, null, 2)}
                        compareMethod={DiffMethod.LINES}
                      />
                    ),
                  })
                }
              >{`View changes`}</Button>
            </Col>
            <Col>
              <a target="_blank" href={createHitchUrl(hitch, resource)} rel="noreferrer">
                <Button type="primary">{`View ${resource.resource}`}</Button>
              </a>
            </Col>
          </Row>
        </Col>
      </Card>
    </Col>
  );
};

const ResourceDelete = ({
  hitch,
  createdAt,
  resource,
}: {
  hitch: string;
  createdAt?: Date;
  resource: DeleteResource;
}) => {
  return (
    <Col>
      <Card>
        <Col>
          <h3>
            {capitalize(resource.resource)} deleted in {hitch}
          </h3>
        </Col>
        {createdAt && <Col>{capitalize(formatRelative(createdAt, new Date()))}</Col>}
        <Col span={24}>
          <Row gutter={[32, 32]}>
            <Col>
              <Button
                type="primary"
                onClick={() =>
                  Modal.info({
                    width: '80%',
                    title: 'Modified by Hitch and Hike',
                    content: (
                      <ReactDiffViewer
                        oldValue={JSON.stringify(JSON.parse(resource.original), null, 2)}
                        newValue={JSON.stringify({})}
                      />
                    ),
                  })
                }
              >{`View deleted resource`}</Button>
            </Col>
          </Row>
        </Col>
      </Card>
    </Col>
  );
};

const ResourceEventView = ({
  hikeId,
  triggerEventId,
  resourceEvent,
  resourceType,
  result,
}: {
  hikeId: string;
  triggerEventId: string;
  resourceEvent?: ResourceEvent | ResourceLinkedEvent;
  resourceType: string;
  result?: ErrorResourceResult | OtherResourceResult;
}): JSX.Element | null => {
  if (!resourceEvent) {
    if (!result) {
      return null;
    }
    switch (result.status) {
      case ResourceResultStatus.SKIP:
        return (
          <ResourceSkip msg={(result as ErrorResourceResult).msg} resourceType={resourceType} />
        );
      case 'error':
      case 'warning':
        return (
          <ResourceError
            hikeId={hikeId}
            triggerEventId={triggerEventId}
            status={result.status}
            error={(result as ErrorResourceResult).error}
            msg={(result as ErrorResourceResult).msg}
            resourceType={resourceType}
          />
        );
      default:
        return null;
    }
  }

  const createResourceComponent = () => {
    switch (resourceEvent.resource.type) {
      case 'create':
        return (
          <ResourceCreate
            key={resourceEvent.resource.id}
            resource={resourceEvent.resource}
            createdAt={(resourceEvent as ResourceEvent).createdAt}
            hitch={resourceEvent.hitch}
          />
        );
      case 'update':
        return (
          <ResourceUpdate
            key={resourceEvent.resource.id}
            resource={resourceEvent.resource}
            createdAt={(resourceEvent as ResourceEvent).createdAt}
            hitch={resourceEvent.hitch}
          />
        );

      case 'delete':
        return (
          <ResourceDelete
            key={resourceEvent.resource.id}
            resource={resourceEvent.resource}
            createdAt={(resourceEvent as ResourceEvent).createdAt}
            hitch={resourceEvent.hitch}
          />
        );
      case 'none':
        return (
          <ResourceNone
            key={resourceEvent.resource.id}
            resource={resourceEvent.resource}
            createdAt={(resourceEvent as ResourceEvent).createdAt}
            hitch={resourceEvent.hitch}
          />
        );
      default:
        return <></>;
    }
  };

  return <>{createResourceComponent()}</>;
};

export default ResourceEventView;
